import { registerHandler } from "../handler";
import { isAffiliateWithoutTrial } from "../../userSegment";
import Cookies from "js-cookie";
import uuid from "uuid";
import { shouldFireMedPixel } from "src/utils/userSegment/features";
import { MedProductType } from "src/utils/constants";
import { MED_SKU } from "src/utils/medEligibility";

// Note(George): In the Impact case accepting T&C is referred as a Med Lead
const MED_LEAD_ACTION_TRACKER_ID = 51560;
const HRT_LEAD_ACTION_TRACKER_ID = 56565;
const WEIGHT_LEAD_ACTION_TRACKER_ID = 51561;

declare const ire: any;

/**
 * Maps MED_SKU to Impact Radius SKU format with plan suffix
 * Returns mapped string if we have valid MED_SKU mapping and plan suffix
 * Returns medPlanType fallback otherwise
 */
export function mapPlanIdToImpactSKUId(
  planId: string,
  medPlanType: MED_SKU
): string {
  // Mapping provided by marketing - TypeScript ensures all MED_SKU values are covered
  const medSkuMappings: Record<MED_SKU, string> = {
    [MED_SKU.BRANDED]: "nmb",
    [MED_SKU.BRANDED_ZEPBOUND_VIAL]: "nmb", // Same as BRANDED
    [MED_SKU.COMPOUNDED]: "nmc",
    [MED_SKU.COMPOUNDED_MICRODOSE]: "nmc", // Same as COMPOUNDED
    [MED_SKU.GENERIC_LIRAGLUTIDE]: "nmcl",
    [MED_SKU.HRT_CREAM]: "nmmc",
    [MED_SKU.HRT_PATCH]: "nmmp",
    [MED_SKU.ORALS_METFORMIN]: "nmom",
  };

  const parts = planId.split("__");
  const suffix = parts.slice(1).join("__");

  // If no suffix, return fallback
  if (!suffix) {
    return medPlanType;
  }

  const mappedPrefix = medSkuMappings[medPlanType];
  return `${mappedPrefix}__${suffix}`;
}

export default function registerHandlers() {
  registerHandler("init", (): void => {
    const searchParams = new URLSearchParams(window.location.search);
    const cid = searchParams.get("cid");
    const sp = searchParams.get("sp");
    if (cid && sp === "impact") {
      Cookies.set("ircid", cid, { expires: 30 });
    }
  });

  registerHandler("lead", ({ emailHashes, userId }) => {
    ire("identify", {
      customerId: userId,
      customerEmail: emailHashes.sha1,
    });
    if (!shouldFireMedPixel()) {
      const payload = {
        orderId: uuid.v4(),
        customerId: userId,
        customerEmail: emailHashes.sha1,
      };
      ire("trackConversion", WEIGHT_LEAD_ACTION_TRACKER_ID, payload, {
        verifySiteDefinitionMatch: true,
      });
    }
  });

  registerHandler(
    "purchase",
    async ({ order, plan, emailHashes, userId, promoCode }) => {
      const { discounted_plan_price, price } = plan;

      let subTotal = price || 0;

      if (isAffiliateWithoutTrial()) {
        subTotal = discounted_plan_price;
      }

      let text1 = "Trial";
      // All these params are applying when user subscribes with trial
      let category = "Trial";

      // Override in case the plan has no trial
      if (!plan.has_trial) {
        text1 = "Sale";
        category = "Subscribed";
      }

      const payload = {
        orderId: order.subscriptionId,
        customerId: userId,
        customerStatus: "New",
        customerEmail: emailHashes.sha1,
        currencyCode: plan.currency,
        text1,
        orderPromoCode: promoCode.promoCodeApplied,
        items: [
          {
            subTotal,
            category,
            sku: plan.braintree_id,
            quantity: 1,
          },
        ],
      };

      const conversionTag = category === "Trial" ? 15420 : 32431;
      ire("trackConversion", conversionTag, payload, {
        verifySiteDefinitionMatch: true,
      });
    }
  );

  registerHandler(
    "purchaseIntent",
    async ({
      emailHashes,
      userId,
      medProductType,
      plan,
      estimatedValue,
      medPlanType,
    }) => {
      const actionTrackerId =
        medProductType === MedProductType.HRT
          ? HRT_LEAD_ACTION_TRACKER_ID
          : MED_LEAD_ACTION_TRACKER_ID;

      const payload = {
        orderId: uuid.v4(),
        customerId: userId,
        customerEmail: emailHashes.sha1,
        items: [
          {
            subTotal: estimatedValue,
            category: "Trial",
            sku: mapPlanIdToImpactSKUId(plan.braintree_id, medPlanType),
            quantity: 1,
          },
        ],
      };

      ire("trackConversion", actionTrackerId, payload, {
        verifySiteDefinitionMatch: true,
      });
    }
  );
}
